import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from './api';
import { useSessao } from './sessao';

/**
 * «Entrar como» — a troca rápida de usuário.
 *
 * «configure a troca rápida de usuário para validação do processo e suba
 *  para iniciarmos os testes» — Humberto, 30/08/2026.
 *
 * ── Por que este recurso existe ──────────────────────────────────────
 *
 * O §12 e o §15 exigem TRÊS pessoas para um ensaio sair: quem executa,
 * quem verifica, quem aprova. Conferir o fluxo inteiro numa sessão
 * exigiria sair, entrar, sair e entrar de novo, com três senhas — e na
 * prática isso vira «testar com um usuário só», que confere qualquer
 * coisa menos o processo.
 *
 * ── As duas peças, e por que a faixa não é enfeite ───────────────────
 *
 * O seletor é discreto: mora na engrenagem, e só o admin o enxerga.
 *
 * A **faixa** é o contrário: fixa no topo, atravessando a tela inteira,
 * impossível de não ver. Ela existe porque o defeito perigoso deste
 * recurso não é técnico — é humano. Alguém veste o técnico de
 * demonstração, atende o telefone, volta e lança um ensaio de verdade
 * achando que é ele mesmo. A faixa é o que impede meia hora de trabalho
 * assinada pela pessoa errada.
 *
 * A trilha grava as duas pessoas de qualquer jeito (`auditoria.atuando_por`,
 * migração 0079), então o registro nunca mente. A faixa é para que
 * ninguém precise ir à trilha descobrir.
 */

export interface Persona {
  id: string;
  login: string;
  nome: string;
  perfil: string;
  perfil_nome: string;
  acoes: number;
  ultimo_ator: string | null;
}

/**
 * A faixa fixa do topo.
 *
 * Renderiza `null` na sessão normal — que é o caso de sempre. Só aparece
 * quando há persona vestida, e aí ocupa a largura toda.
 */
export function FaixaDaPersona() {
  const { eu, voltarASerEu } = useSessao();
  const [voltando, setVoltando] = useState(false);

  if (!eu?.atuandoPor) return null;

  return (
    <div className="faixa-persona" role="status">
      <span className="faixa-persona-txt">
        Você está atuando como <b>{eu.nome}</b>
        <em> ({eu.perfil})</em>
        {eu.atorNome && <> — você é <b>{eu.atorNome}</b></>}
      </span>
      <button
        type="button"
        className="botao pequeno"
        disabled={voltando}
        onClick={() => { setVoltando(true); void voltarASerEu(); }}
      >
        {voltando ? 'voltando…' : 'voltar a ser eu'}
      </button>
    </div>
  );
}

/**
 * O seletor.
 *
 * Lista fechada, vinda de `vw_persona`: dez contas de demonstração, uma
 * por tipo de usuário (a H1, onda 295, deu persona aos cinco tipos que não
 * tinham — D11), na ordem do fluxo — de quem programa a quem só lê. Não há
 * busca por usuário, e é de propósito: vestir a conta de uma pessoa REAL
 * seria assinar no lugar dela.
 *
 * Vestida a persona, a tela vai à porta DELA (`/`), como se ela tivesse
 * acabado de entrar: desde a H1 cada tipo tem os seus espaços, e ficar na
 * tela em que o administrador estava mostraria, ao sondador, a recusa do Hub
 * (ponto de atenção 2 do plano da H1).
 */
export function EscolherPersona({ fechar }: { fechar: () => void }) {
  const { eu, entrarComo } = useSessao();
  const navegar = useNavigate();
  const [lista, setLista] = useState<Persona[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [indo, setIndo] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api<Persona[]>('/auth/personas')
      .then((r) => { if (vivo) setLista(r); })
      .catch((e) => { if (vivo) setErro(String(e.message ?? e)); });
    return () => { vivo = false; };
  }, []);

  const vestir = useCallback(async (p: Persona) => {
    setIndo(p.id);
    setErro(null);
    try {
      await entrarComo(p.id);
      fechar();
      navegar('/');
    } catch (e) {
      setErro((e as Error).message);
      setIndo(null);
    }
  }, [entrarComo, fechar, navegar]);

  useEffect(() => {
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape') fechar(); };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [fechar]);

  return (
    <div className="veu-modal" role="dialog" aria-modal="true"
      aria-label="Entrar como">
      <div className="caixa-persona">
        <h2>Entrar como</h2>
        <p className="sub">
          Contas de demonstração, uma por perfil. Servem para conferir o
          processo pelos olhos de quem executa, de quem verifica e de quem
          aprova — sem sair da sessão. A trilha registra as duas pessoas:
          a persona e você.
        </p>

        {erro && <p className="erro">{erro}</p>}
        {!lista && !erro && (
          <span className="carregando" role="status" aria-label="Carregando" />
        )}

        <ul className="lista-persona">
          {(lista ?? []).map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="persona"
                disabled={!!indo || p.id === eu?.usuarioId}
                onClick={() => void vestir(p)}
              >
                <span className="persona-nome">{p.perfil_nome}</span>
                <span className="sub mono">{p.login}</span>
                {/*
                  * Quem já vestiu esta persona, e quantas vezes.
                  *
                  * A lista não esconde o uso: uma conta de demonstração
                  * com trezentas ações registradas não é mais uma conta de
                  * demonstração, e quem administra precisa ver isso sem
                  * abrir a auditoria.
                  */}
                {p.acoes > 0 && (
                  <span className="meta">
                    {p.acoes} ação(ões)
                    {p.ultimo_ator && ` · último: ${p.ultimo_ator}`}
                  </span>
                )}
                {p.id === eu?.usuarioId && (
                  <span className="meta">é quem você está vestindo agora</span>
                )}
              </button>
            </li>
          ))}
        </ul>

        <div className="acoes">
          <button type="button" className="botao" onClick={fechar}>
            cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
